// The Projects pages' pure parts (design §7.2–§7.4; owner rulings of 2026-10-02, ADR 0017): the list's filters in the
// URL, its columns and the `/api/projects` query, the empty state's truth, a share's state words (05-content §6) and
// every sentence a project dialog shows — held here, DOM-free, so each is unit-tested under node and quoted in one
// place. Nothing here names a project the server did not send; nothing here reads a store.
import type { ProjectDetail, ProjectListItem, ProjectListResponse, ProjectShareDto, RenameProjectRequest, SharedTodosResponse, SharePartyDto, SortDirection } from '../../../shared/api-types.js';
import { PROJECT_SHARE_STATE_LABELS, PROJECT_SORT_KEYS, isOneOf, type ProjectKind, type ProjectSetBy, type ProjectShareState } from '../../../shared/enums.js';
import { PROJECT_LABEL_SLOTS } from '../../../shared/projects.js';
import { WITHHELD_COLLEAGUE_OBJECT } from '../../../shared/text.js';
import { queryString } from '../../api.js';
import { formatList, pluralise } from '../../ui/format.js';
import type { IconName } from '../../ui/Icon.js';
import type { SortParamOptions, SortState } from '../../ui/sort-params.js';
import type { Tone } from '../../ui/StatusPill.js';
import { SHARE_EXECUTIVE_CLAUSE } from './executive-disclosure.js';

// --- The list's filters (04-data §1.5; 03-components §10) -----------------------------------------------------------

/** The Kind filter: every project, the person's own, or those shared with them (`GET /api/projects?kind=`). */
export const PROJECT_KIND_FILTERS = ['all', 'own', 'shared'] as const;
export type ProjectKindFilter = (typeof PROJECT_KIND_FILTERS)[number];

export const PROJECT_KIND_FILTER_LABELS: Readonly<Record<ProjectKindFilter, string>> = {
  all: 'All',
  own: 'Yours',
  shared: 'Shared with you',
};

/** A row's kind as the table and its chip say it (text, never colour alone). */
export const PROJECT_KIND_LABELS: Readonly<Record<ProjectKind, string>> = {
  own: 'Yours',
  shared: 'Shared with you',
};

export interface ProjectFilters {
  q: string;
  kind: ProjectKindFilter;
}

export const DEFAULT_PROJECT_FILTERS: Readonly<ProjectFilters> = Object.freeze({ q: '', kind: 'all' });

/** The URL keys; the sort lives beside them under the table's own namespace (`projects_sort`, `projects_dir`). */
export const PROJECT_FILTER_PARAMS = Object.freeze({ q: 'q', kind: 'kind' });

/** Reads the filters from the URL; a damaged value degrades to the default rather than an error. */
export function parseProjectFilters(params: URLSearchParams): ProjectFilters {
  const kind = params.get(PROJECT_FILTER_PARAMS.kind);
  return {
    q: (params.get(PROJECT_FILTER_PARAMS.q) ?? '').trim(),
    kind: isOneOf(PROJECT_KIND_FILTERS, kind) ? kind : DEFAULT_PROJECT_FILTERS.kind,
  };
}

/** Writes the filters back; defaults are written as an absence so a pristine URL stays clean. */
export function writeProjectFilters(params: URLSearchParams, filters: ProjectFilters): URLSearchParams {
  const next = new URLSearchParams(params);
  if (filters.q.trim() !== '') next.set(PROJECT_FILTER_PARAMS.q, filters.q.trim());
  else next.delete(PROJECT_FILTER_PARAMS.q);
  if (filters.kind !== DEFAULT_PROJECT_FILTERS.kind) next.set(PROJECT_FILTER_PARAMS.kind, filters.kind);
  else next.delete(PROJECT_FILTER_PARAMS.kind);
  return next;
}

/** How many filters narrow the list beyond the default view. */
export function activeProjectFilterCount(filters: ProjectFilters): number {
  return (filters.q.trim() !== '' ? 1 : 0) + (filters.kind !== DEFAULT_PROJECT_FILTERS.kind ? 1 : 0);
}

// --- The list's columns and query (04-data §1.2) ----------------------------------------------------------------------

/** Every column of the Projects table, in order; every one sorts (§1.2), the row-actions column apart. */
export const PROJECT_COLUMN_KEYS = ['name', 'kind', 'meetings', 'open_todos', 'last_used_at', 'named_by', 'shared_with'] as const;
export type ProjectColumnKey = (typeof PROJECT_COLUMN_KEYS)[number];

/** The declared default, visible on the header (§1.2.1): most recently used first — the order the AI's fence sends names in. */
export const PROJECT_DEFAULT_SORT: SortState<ProjectColumnKey> = Object.freeze({ key: 'last_used_at', dir: 'desc' });

/** The sort's URL namespace: `projects_sort` / `projects_dir`, as design §7.2 addresses it. */
export const PROJECT_SORT_NAMESPACE = 'projects';

export const PROJECT_SORT_OPTIONS: SortParamOptions<ProjectColumnKey> = Object.freeze({
  namespace: PROJECT_SORT_NAMESPACE,
  keys: PROJECT_COLUMN_KEYS,
  defaultSort: PROJECT_DEFAULT_SORT,
});

/**
 * The `/api/projects` query for a filter + sort. Why only the server's keys travel: the route orders the whole own+shared
 * set by the keys it knows (`PROJECT_SORT_KEYS`, nulls last) and the table re-sorts the loaded rows for the rest (kind,
 * shared with), so every column sorts and a server-sorted column is right even past the row cap.
 */
export function projectListPath(filters: ProjectFilters, sort: SortState<ProjectColumnKey>): string {
  const serverSort = isOneOf(PROJECT_SORT_KEYS, sort.key) ? sort : null;
  return `/api/projects${queryString({
    q: filters.q.trim() || null,
    kind: filters.kind === 'all' ? null : filters.kind,
    sort: serverSort?.key ?? null,
    dir: serverSort ? (serverSort.dir as SortDirection) : null,
  })}`;
}

/** One project's API path; the id is encoded once, here. */
export function projectApiPath(id: string): string {
  return `/api/projects/${encodeURIComponent(id)}`;
}

/** A share's sharer-side path (`POST …/merge-back`, `…/unmerge-back`, `DELETE`): the project id travels in the path, as the server built it. */
export function projectSharePath(projectId: string, shareId: string, act?: 'merge-back' | 'unmerge-back'): string {
  return `${projectApiPath(projectId)}/shares/${encodeURIComponent(shareId)}${act ? `/${act}` : ''}`;
}

/** A share's recipient-side path (`POST /api/projects/shares/:shareId/{merge-in,unmerge-in,leave}`). */
export function shareActPath(shareId: string, act: 'merge-in' | 'unmerge-in' | 'leave'): string {
  return `/api/projects/shares/${encodeURIComponent(shareId)}/${act}`;
}

/** Who named a project, as the table's cell says it; null on a shared row, where provenance is not the reader's to see. */
export function namedByWord(namedBy: ProjectSetBy | null): 'Calis' | 'You' | null {
  if (namedBy === 'ai') return 'Calis';
  if (namedBy === 'person') return 'You';
  return null;
}

/** The colour row of R2, as text: a slot is never conveyed by colour alone (01-foundations §1.8). */
export function colourSlotWords(slot: number): string {
  return `slot ${slot} of ${PROJECT_LABEL_SLOTS}`;
}

// --- The empty state, truthful to the backfill (design §7.2; 04-data §1.3) -----------------------------------------------

export const NO_PROJECTS_TITLE = 'No projects yet';

/**
 * One sentence per truth the route answers (`ProjectListResponse.backfill`): `pending` — the landing backfill will reach
 * this person; `done` — it has, and Calis names projects as it summarises from here; `none` — no grant, AI off, the floor
 * off or the backfill paused, so no meeting is sorted for them unless by hand (their to-dos may still be, by the sorting
 * pass — `sort`, ADR 0018). Each carries the New project action.
 */
export const PROJECTS_EMPTY_SENTENCES: Readonly<Record<ProjectListResponse['backfill'], string>> = {
  pending: 'Calis is sorting your recent meetings into projects now; you can also add one.',
  done: 'Calis names projects as it summarises your meetings; you can also add one now.',
  none: 'Add a project and file meetings and to-dos under it.',
};

export function projectsEmptyState(backfill: ProjectListResponse['backfill']): { title: string; description: string } {
  return { title: NO_PROJECTS_TITLE, description: PROJECTS_EMPTY_SENTENCES[backfill] };
}

// --- A share's state (05-content §6) ------------------------------------------------------------------------------------

export interface ShareStatePresentation {
  /** The state word: Active, Inactive or Cancelled — never "merged in" or "left", which are meta beside it. */
  label: string;
  tone: Tone;
  icon: IconName;
}

export function shareStatePresentation(state: ProjectShareState): ShareStatePresentation {
  const label = PROJECT_SHARE_STATE_LABELS[state];
  switch (state) {
    case 'standing':
    case 'merged_in':
    case 'merged_back':
      return { label, tone: 'success', icon: 'check' };
    case 'inactive':
      return { label, tone: 'warning', icon: 'alert-triangle' };
    case 'left':
    case 'revoked':
    default:
      return { label, tone: 'neutral', icon: 'minus' };
  }
}

/**
 * What an inactive share's row says on either side: the other party is not admitted at this read, so nothing crosses.
 * Why "no longer has access" (the owner's ruling of 2026-10-11, "A colleague (no longer has access)" everywhere — "the
 * same words on the shares table and the People tab as in the history", extending D33): the party itself now reads "A
 * colleague (no longer has access)" there, from the server (`SharePartyDto.withheld`), and the state's sentence says the
 * same fact in the same words; it read "A colleague no longer uses Calis here".
 */
export const INACTIVE_SHARE_SENTENCE = 'This colleague no longer has access';

/**
 * What a shared project's To-dos and Meetings tabs say while the share is inactive: nothing crosses. Why these words (the
 * owner's ruling of 2026-10-11 on the readings Phase 2d left, "Align the other tabs' words"): the People tab says the
 * state as "This colleague no longer has access", and the other tabs now say the same fact in the same words; they read
 * "Nothing crosses while the sharer is not using Calis here." One sentence, whole, for both tabs.
 */
export const INACTIVE_SHARE_NOTHING_CROSSES = 'Nothing crosses while this colleague no longer has access.';

/**
 * The meta words beside a share's state pill on the sharer's People tab: what the recipient did with it, or how it ended.
 * The recipient's project name never appears — the sharer learns only that it was merged in (design §11 item 15).
 */
export function shareMetaWords(share: Pick<ProjectShareDto, 'state' | 'mergedIn' | 'mergedBack' | 'recipient'>): string[] {
  switch (share.state) {
    case 'left':
      return [`${share.recipient.displayName} left`];
    case 'revoked':
      return ['by you'];
    case 'inactive':
      return [INACTIVE_SHARE_SENTENCE];
    default: {
      const words: string[] = [];
      if (share.mergedIn) words.push('merged in');
      if (share.mergedBack) words.push('merged back');
      return words;
    }
  }
}

/** The sentence under a standing share on the sharer's side, or null when there is nothing beyond the state. */
export function sharerShareSentence(share: Pick<ProjectShareDto, 'state' | 'mergedIn' | 'mergedBack' | 'recipient'>): string | null {
  if (share.state === 'inactive') return INACTIVE_SHARE_SENTENCE;
  if (share.state === 'left' || share.state === 'revoked' || !share.mergedIn) return null;
  const grouped = `${share.recipient.displayName} grouped this with one of their projects`;
  return share.mergedBack ? `${grouped}, and you group their project’s own meetings and to-dos here` : grouped;
}

// --- The dialogs' sentences (design §7.4; the orchestrator's directives §4 ruling on what crosses) ---------------------

/**
 * What never crosses on a share. Why this sentence and not the design's ("That includes to-dos Calis made for you from
 * Slack, by their wording only"): the share store's statement 16 keeps a Slack check's and the daily sweep's to-dos home,
 * and a brief's that drew on Slack material (the orchestrator's ruling under directives §4) — the copy says what the code
 * does.
 */
export const SHARE_NEVER_SENTENCE = 'Never to-dos Calis made for you from Slack, your Slack findings, notes or transcripts.';

/**
 * The Share dialog's disclosure, naming the recipient once chosen (S1's list, read live, ended by the sharer alone — S7).
 * While executive intelligence is on it gains the design's clause that the project's name may now appear in the
 * executives' report (§2.14 item 9; `SHARE_EXECUTIVE_CLAUSE`): the share is the act that makes the name eligible.
 */
export function shareDisclosureText(recipientName: string | null, executiveOn = false): string {
  const who = recipientName ?? 'The person you choose';
  const report = executiveOn ? ` ${SHARE_EXECUTIVE_CLAUSE}` : '';
  return `${who} will see this project’s meetings — their titles, dates, attendee names, summaries, key points and decisions — and its to-dos — text, status, due date, who is responsible — read live for as long as you share it${report}. ${SHARE_NEVER_SENTENCE} Only you can end the share.`;
}

/** The consent sentence of the Merge in dialog, verbatim (S6: a merge-in is the recipient's consent to the sharer's merge-back). */
export function mergeInConsentText(sharerName: string): string {
  return `Merging in groups ${sharerName}’s shared project with yours. By merging it in you agree that ${sharerName} may group your project’s own meetings and to-dos with theirs in return, without asking you again — until you unmerge or leave, which withdraws that. Nothing of yours is copied: they read it live while the share stands, and never your Slack findings, notes or transcripts.`;
}

/** Names joined as the locale joins a list (05-content §8; the record's "projects' name join", Phase 1c D30) — never by hand. */
function joinNames(names: readonly string[]): string {
  return formatList(names);
}

/** "1 meeting", "3 meetings": the count and its noun as one complete form, chosen by the plural rule (05-content §8). */
function countWord(count: number, one: string, other: string): string {
  return pluralise(count, { one: `# ${one}`, other: `# ${other}` });
}

export interface MergeDialogInput {
  /** The project merged away (B), with its counts and shares. */
  absorbed: Pick<ProjectDetail, 'name' | 'counts' | 'shares' | 'mergedInShares'>;
  /** The survivor (A), by name; its own shares tell which recipients already see it. */
  into: { name: string; shares: readonly Pick<ProjectShareDto, 'recipient' | 'state'>[] | null };
}

/** Whether a share still crosses: standing, merged in or merged back — not left, revoked or inactive. */
export function shareStands(state: ProjectShareState): boolean {
  return state === 'standing' || state === 'merged_in' || state === 'merged_back';
}

/**
 * Whether the sharer may still end a share (S7: only the sharer revokes). Why wider than `shareStands`: an `inactive`
 * share is not an ended one — the recipient is merely not admitted at this read (suspended, say) and the share resumes
 * crossing the moment they are readmitted (`sharesForRecipient` keys on revoked and left alone), and the server's
 * DELETE revokes any unrevoked share. Only a revoked or left share is finished.
 */
export function shareRevocable(state: ProjectShareState): boolean {
  return state !== 'revoked' && state !== 'left';
}

/**
 * The sentence a sharer-side recipient's grouping ends with on a merge of the recipient's own projects. Why "any
 * merge-back … is withdrawn" and not the design's "stops grouping your rows": the server withdraws a merge-back only
 * where one stood (`routes/projects.ts`: "A regrouped share without a merge-back changes nothing the sharer can see")
 * and `ProjectDetail.mergedInShares` carries no merge-back flag, so a sentence asserting the sharer was grouping would
 * be false for the common merged-in-only share.
 */
export function mergedInRegroupedSentence(sharerName: string, absorbedName: string, intoName: string, sharerWithheld = false): string {
  // Why its own sentence for a withheld sharer (the owner's ruling of 2026-10-11, "A colleague (no longer has access)"
  // everywhere): the stated withholding is never made possessive nor repeated as a name.
  if (sharerWithheld) return `The project ${WITHHELD_COLLEAGUE_OBJECT} shared with you, grouped under ${absorbedName}, will be grouped under ${intoName}; any merge-back they made is withdrawn until they merge back again.`;
  return `${sharerName}’s project you grouped under ${absorbedName} will be grouped under ${intoName}; any merge-back ${sharerName} made is withdrawn until they merge back again.`;
}

/**
 * A shared project grouped under one of the reader's own, as the record page's "Grouped here" lists it: the sharer's
 * name and the project's — or, while the sharer is withheld, the project and the stated withholding, never the words
 * made possessive (the owner's ruling of 2026-10-11, "A colleague (no longer has access)" everywhere).
 */
export function groupedHereWords(entry: { sharedBy: SharePartyDto; projectName: string }): string {
  return entry.sharedBy.withheld === true ? `“${entry.projectName}”, shared by ${WITHHELD_COLLEAGUE_OBJECT}` : `${entry.sharedBy.displayName}’s “${entry.projectName}”`;
}

/**
 * A share's party as a sentence names them after its first word — "Stop sharing Alpine with …", "shared by …": their
 * display name, or, while the server says they are withheld, the stated withholding in its mid-sentence form. Why
 * (the owner's ruling of 2026-10-11, "A colleague (no longer has access)" everywhere; `src/shared/text.ts`: the client
 * never splices the capitalised label into a sentence): the history writes "with a colleague (no longer has access)",
 * and the share's own sentences now say the same words the same way. Where the party stands alone — a table cell, the
 * "Shared by" field, a sentence's first words — the server's `displayName` is shown as sent.
 */
export function partyInSentence(party: Pick<SharePartyDto, 'displayName' | 'withheld'>): string {
  return party.withheld === true ? WITHHELD_COLLEAGUE_OBJECT : party.displayName;
}

/**
 * The Merge dialog's sentences (design §7.4): what moves, then — when B has live shares — who sees A instead, whose
 * duplicate share ends, and whose share ends because they left A earlier (shares travel on a merge, and the sharer
 * must be told; ADR 0014 as amended, S1); then, on the recipient's side, which shared projects grouped under B move
 * under A with the sharer's merge-back withdrawn.
 *
 * Why a LEFT share of A counts: the store's merge (`stores/projects.ts`) takes every unrevoked share of the survivor
 * as a recipient who already holds one — left included, since the live index is on `revoked_at` alone — and closes
 * the source share to such a recipient rather than re-pointing it; that person then sees nothing of A.
 */
export function mergeDialogSentences(input: MergeDialogInput): string[] {
  const { absorbed, into } = input;
  const counts = absorbed.counts;
  const moved = counts
    ? `Its ${countWord(counts.meetings, 'meeting', 'meetings')}, ${countWord(counts.todos, 'to-do', 'to-dos')} and Slack filings move to ${into.name}; its name stays as a former name of ${into.name}.`
    : `Its meetings, to-dos and Slack filings move to ${into.name}; its name stays as a former name of ${into.name}.`;
  const sentences = [moved];
  const live = absorbed.shares.filter((share) => shareStands(share.state));
  if (live.length > 0) {
    const holdsOnSurvivor = new Map<string, ProjectShareState>();
    for (const share of into.shares ?? []) if (share.state !== 'revoked') holdsOnSurvivor.set(share.recipient.id, share.state);
    const left = live.filter((share) => holdsOnSurvivor.get(share.recipient.id) === 'left');
    const continuing = live.filter((share) => holdsOnSurvivor.get(share.recipient.id) !== 'left');
    if (continuing.length > 0) {
      const names = continuing.map((share) => share.recipient.displayName);
      sentences.push(`The ${countWord(continuing.length, 'person', 'people')} you shared it with — ${joinNames(names)} — will see ${into.name} instead.`);
    }
    for (const share of continuing) {
      if (holdsOnSurvivor.has(share.recipient.id)) sentences.push(`${share.recipient.displayName} already sees ${into.name}; the duplicate share ends.`);
    }
    for (const share of left) {
      sentences.push(`${share.recipient.displayName} left ${into.name} earlier, so the share of ${absorbed.name} with them ends.`);
    }
  }
  for (const grouped of absorbed.mergedInShares) {
    sentences.push(mergedInRegroupedSentence(grouped.sharedBy.displayName, absorbed.name, into.name, grouped.sharedBy.withheld === true));
  }
  return sentences;
}

/**
 * The Rename dialog's remedy on a taken name (design §7.4). Why a question and not the fact: the field's own error line
 * already states the server's sentence ("You already have a project called X." / "X is a former name of Y — forget it or
 * merge."), and keeps the field marked invalid for assistive technology; the remedy box carries only what to do next.
 */
export function renameRemedyQuestion(kind: 'live' | 'alias' | 'absorbed'): string {
  switch (kind) {
    case 'live':
      return 'Merge into it instead?';
    case 'alias':
      return 'Forget that name, or merge into it?';
    case 'absorbed':
      return 'Undo that merge from its page, or merge this project into it too.';
  }
}

/**
 * The body of the toast's Undo of a rename, from the detail the page held before the rename and the one the rename
 * answered (owner ruling 19 of 2026-10-04: "the toast's Undo restores the former name and forgets the abandoned one, so
 * 'Also known as' is as it was before the rename"). Why the page decides whether to send `undo: true`: the server cannot
 * tell an Undo from a rename back by hand (which keeps the abandoned name as an alias), and it cannot tell either whether
 * the abandoned name was a former name BEFORE the rename — only the page knows, from the detail it held. So `undo: true`
 * goes exactly when the rename left "Also known as" one name longer: it wrote an alias for the abandoned name and
 * reclaimed none, and the server's Undo — reclaim the former name, write no alias — puts the list back. A rename that
 * reclaimed one of the project's own former names (one alias went, one came) is undone by a plain rename back, which
 * reclaims the former name and keeps the abandoned one as the alias it was; a change of case or punctuation (no alias
 * came) is undone by a plain rename back too, which writes none for a same-key spelling. In each case the list is as it
 * was before the rename. The ruling's two clauses meet in the reclaim case, where "forgets the abandoned one" would
 * shrink the list past where it stood: the outcome clause is followed — recorded for the owner's correction (ADR 0017,
 * ruling 19).
 */
export function undoRenameRequest(before: Pick<ProjectDetail, 'name' | 'aliases'>, renamed: Pick<ProjectDetail, 'name' | 'aliases'>): RenameProjectRequest {
  const held = new Set(before.aliases.map((alias) => alias.id));
  const kept = new Set(renamed.aliases.map((alias) => alias.id));
  const wroteAlias = renamed.aliases.some((alias) => !held.has(alias.id));
  const reclaimed = before.aliases.some((alias) => !kept.has(alias.id));
  return { name: before.name, expectedName: renamed.name, ...(wroteAlias && !reclaimed ? { undo: true as const } : {}) };
}

/**
 * An own project's tab badge (06-modules §5.3 R3: a collection "as honest about its extent as the same records are on
 * their own screen"; "never a 0 before the data"). The To-dos and Meetings tabs show the person's own rows AND every
 * merged-in or merged-back group beneath them, so the badge is the own count plus those rows — and undefined (no
 * badge) while groups are expected but not yet loaded, so a 0 is never shown above seven rows.
 */
export function ownTabCount(own: number | undefined, expectsGroups: boolean, groupRows: number | undefined): number | undefined {
  if (own === undefined) return undefined;
  if (!expectsGroups) return own;
  return groupRows === undefined ? undefined : own + groupRows;
}

/** The Delete dialog's body: a label goes, nothing else (ruling 10, "delete detaches then deletes"). */
export const DELETE_PROJECT_SENTENCE = 'Its meetings, to-dos and Slack filings keep everything and lose only this label; its former names go too, and anyone you shared it with loses the view. Calis may name it again later.';

export function deleteProjectTexts(name: string): { title: string; body: string; confirm: string } {
  return { title: `Delete ${name}?`, body: DELETE_PROJECT_SENTENCE, confirm: 'Delete' };
}

/**
 * Stop sharing: irreversible for the recipient, so it confirms (03-components §4.2), and `done` is the toast after it.
 * Why the flag (the owner's ruling of 2026-10-11, "A colleague (no longer has access)" everywhere): Stop sharing is
 * offered on an inactive share, whose recipient the server names with the withheld words; mid-sentence they read as the
 * history writes them (`partyInSentence`), never "with A colleague (no longer has access)".
 */
export function stopSharingTexts(projectName: string, recipientName: string, recipientWithheld = false): { title: string; body: string; confirm: string; done: string } {
  const recipient = partyInSentence({ displayName: recipientName, withheld: recipientWithheld });
  return {
    title: `Stop sharing ${projectName} with ${recipient}?`,
    body: 'They lose the view at once, and any grouping they made of it ends.',
    confirm: 'Stop sharing',
    done: `Stopped sharing with ${recipient}.`,
  };
}

/**
 * Leave: only the sharer can share it again, so it confirms. Why another title for a withheld sharer (the owner's ruling
 * of 2026-10-11, "A colleague (no longer has access)" everywhere): the words are a stated withholding, never a name to
 * make possessive — "Leave A colleague (no longer has access)’s …" reads as a person called that.
 */
export function leaveShareTexts(sharerName: string, projectName: string, sharerWithheld = false): { title: string; body: string; confirm: string } {
  return {
    title: sharerWithheld ? `Leave ${projectName}, shared by ${WITHHELD_COLLEAGUE_OBJECT}?` : `Leave ${sharerName}’s ${projectName}?`,
    body: 'Your grouping of it and their merge-back end; only they can share it with you again.',
    confirm: 'Leave',
  };
}

/** Forgetting an absorbed alias destroys the merge's Undo, so it confirms where a renamed-away name is one click. */
export function forgetAbsorbedTexts(absorbedName: string, survivorName: string): { title: string; body: string; confirm: string } {
  return {
    title: `Forget ${absorbedName}?`,
    body: `Its merge into ${survivorName} can no longer be undone.`,
    confirm: 'Forget',
  };
}

/** The accessible name of a former name's one-click remove control. */
export function forgetNameLabel(name: string): string {
  return `Forget the name ${name}`;
}

/** The toast on arriving at an absorbed project's id, and the merge's own: the page lands on the survivor and says so. */
export function mergedIntoToast(survivorName: string): string {
  return `Merged into ${survivorName}`;
}

// --- Captions and words of the record page's tabs ----------------------------------------------------------------------

export type SharedGroup = SharedTodosResponse['groups'][number];

/**
 * The caption of a shared to-dos table under an own project: the sharer's project name for a merged-in group (S1 grants
 * it to the recipient; `ProjectDetail.mergedInShares` carries it) and the recipient's own rows for a merged-back one —
 * never the recipient's project name, which is theirs.
 */
export function sharedTodosCaption(group: Pick<SharedGroup, 'shareId' | 'sharedBy' | 'direction'>, projectNames: ReadonlyMap<string, string>): string {
  if (group.direction === 'merged_back') return `${group.sharedBy.displayName}’s own to-dos of this project`;
  const name = projectNames.get(group.shareId);
  return name ? `From ${group.sharedBy.displayName}’s “${name}”` : `Shared by ${group.sharedBy.displayName}`;
}

export function sharedMeetingsCaption(group: Pick<SharedGroup, 'sharedBy' | 'direction'>): string {
  return group.direction === 'merged_back' ? `${group.sharedBy.displayName}’s own meetings of this project` : `Shared by ${group.sharedBy.displayName}`;
}

/**
 * The R2 digest's words: the record's name, then what is in it where the counts are the reader's to see. Why
 * `partyInSentence` (the owner's ruling of 2026-10-11, "A colleague (no longer has access)" everywhere): a sharer the
 * server says is withheld reads "shared by a colleague (no longer has access)", as the history writes it.
 */
export function projectDigest(project: Pick<ProjectListItem, 'name' | 'kind' | 'meetings' | 'openTodos' | 'sharedBy'>): string {
  if (project.kind === 'shared') return project.sharedBy ? `${project.name} · shared by ${partyInSentence(project.sharedBy)}` : project.name;
  const parts = [project.name];
  if (project.meetings !== null) parts.push(countWord(project.meetings, 'meeting', 'meetings'));
  if (project.openTodos !== null) parts.push(countWord(project.openTodos, 'open to-do', 'open to-dos'));
  return parts.join(' · ');
}

/** The one state pill R1 carries, or none (06-modules §5.10: at most one; 03-components §9: a pill is a state, not provenance). */
export function projectStatePill(project: Pick<ProjectListItem, 'kind' | 'sharedWith'>): string | null {
  if (project.kind === 'shared') return 'Shared with you';
  if (project.sharedWith > 0) return `Shared with ${project.sharedWith}`;
  return null;
}

/** The record page's tabs (design §7.3 R3), in order; the Slack tab is offered only where the meeting page would offer its own. */
export const PROJECT_TABS = ['todos', 'meetings', 'slack', 'people', 'activity'] as const;
export type ProjectTabId = (typeof PROJECT_TABS)[number];

/** The Slack tab's note on a project shared with the reader: nothing of Slack ever crosses (S1). */
export const SLACK_NEVER_SHARED_TITLE = 'Slack findings are never shared';
export const SLACK_NEVER_SHARED_SENTENCE = 'A shared project carries its meetings and to-dos; the sharer’s Slack findings, notes and transcripts stay with them.';

/** The Slack tab's denied row when the Assistant's gate keeps the sweep's matters back (S4). */
export const SLACK_TODAY_WITHHELD_SENTENCE = 'Slack today is withheld: you may not see Slack material at the moment.';
